import { useEffect, useState } from "react";
import {
  mealLogsApi,
  weightLogsApi,
  type DaySummary,
  type User,
  type WeightLog,
} from "../api/client";
import { PageHeader } from "../components/PageHeader";
import { dateTimeLocalToIso, nowDateTimeLocalValue } from "../lib/date";

function daysBefore(n: number): { from: string; to: string } {
  const today = new Date();
  const to = new Date(today);
  const from = new Date(today);
  from.setDate(today.getDate() - n + 1);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { from: fmt(from), to: fmt(to) };
}

const PERIODS: { label: string; days: number }[] = [
  { label: "7日", days: 7 },
  { label: "30日", days: 30 },
  { label: "90日", days: 90 },
];

export function ChartsPage({ user }: { user: User }) {
  const [days, setDays] = useState(30);
  const [calories, setCalories] = useState<DaySummary[]>([]);
  const [weights, setWeights] = useState<WeightLog[]>([]);

  const reloadWeights = (rangeDays: number) => {
    const { from, to } = daysBefore(rangeDays);
    return weightLogsApi.range(from, to).then(setWeights);
  };

  useEffect(() => {
    const { from, to } = daysBefore(days);
    Promise.all([mealLogsApi.range(from, to), weightLogsApi.range(from, to)])
      .then(([c, w]) => {
        setCalories(c);
        setWeights(w);
      })
      .catch(console.error);
  }, [days]);

  return (
    <div className="page">
      <PageHeader title="グラフ" titleClassName="page-title-half" />

      <div style={{ display: "flex", gap: 8, marginBottom: "var(--space-lg)" }}>
        {PERIODS.map((p) => (
          <button
            key={p.days}
            className={p.days === days ? "btn-primary" : "btn-pearl"}
            onClick={() => setDays(p.days)}
            style={{ flex: 1 }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <h2 className="section-title">摂取カロリー</h2>
      <LineChart
        points={calories.map((c) => ({ label: c.date, value: c.total_kcal }))}
        targetLine={user.daily_target_kcal}
        unit="kcal"
      />

      <h2 className="section-title">体重</h2>
      <LineChart
        points={weights.map((w) => ({ label: w.recorded_at.slice(0, 10), value: w.weight_kg }))}
        unit="kg"
      />

      <h2 className="section-title">体重を記録</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        時刻を分けて保存できるので、1日に何回でも記録できます。
      </p>
      <WeightEntry onAdded={() => reloadWeights(days)} />

      <h2 className="section-title">最近の体重記録</h2>
      {weights.length === 0 ? (
        <p className="muted">体重の記録がありません。</p>
      ) : (
        [...weights].reverse().slice(0, 10).map((weight) => (
          <div className="list-item" key={weight.id}>
            <div className="list-item-main">
              <span className="list-item-title">{weight.weight_kg} kg</span>
              <span className="list-item-sub">{formatWeightDateTime(weight.recorded_at)}</span>
            </div>
            <button
              type="button"
              className="btn-link"
              onClick={async () => {
                await weightLogsApi.remove(weight.id);
                await reloadWeights(days);
              }}
            >
              削除
            </button>
          </div>
        ))
      )}
    </div>
  );
}

function formatWeightDateTime(iso: string): string {
  const date = new Date(iso);
  const month = String(date.getMonth() + 1);
  const day = String(date.getDate());
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${month}/${day} ${hours}:${minutes}`;
}

function LineChart({
  points,
  unit,
  targetLine,
}: {
  points: { label: string; value: number }[];
  unit: string;
  targetLine?: number;
}) {
  if (points.length === 0) {
    return <p className="muted">データがありません。</p>;
  }
  const w = 320;
  const h = 160;
  const pad = 24;
  const values = points.map((p) => p.value);
  if (targetLine) values.push(targetLine);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;

  const x = (i: number) =>
    pad + (i * (w - pad * 2)) / Math.max(1, points.length - 1);
  const y = (v: number) => h - pad - ((v - min) / span) * (h - pad * 2);

  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(p.value)}`)
    .join(" ");

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        width="100%"
        style={{ display: "block" }}
        aria-label={`${unit} 推移グラフ`}
      >
        {targetLine !== undefined && (
          <line
            x1={pad}
            x2={w - pad}
            y1={y(targetLine)}
            y2={y(targetLine)}
            stroke="var(--color-hairline)"
            strokeDasharray="4 4"
          />
        )}
        <path d={path} fill="none" stroke="var(--color-primary)" strokeWidth={2} />
        {points.map((p, i) => (
          <circle key={i} cx={x(i)} cy={y(p.value)} r={2.5} fill="var(--color-primary)" />
        ))}
      </svg>
      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        最小 {Math.round(min)} {unit} / 最大 {Math.round(max)} {unit}
      </div>
    </div>
  );
}

function WeightEntry({ onAdded }: { onAdded: () => Promise<void> | void }) {
  const [w, setW] = useState("");
  const [recordedAt, setRecordedAt] = useState(nowDateTimeLocalValue());
  const [saving, setSaving] = useState(false);
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        const num = Number(w);
        if (!num || !recordedAt) return;
        setSaving(true);
        try {
          await weightLogsApi.create({
            recorded_at: dateTimeLocalToIso(recordedAt),
            weight_kg: num,
          });
          setW("");
          setRecordedAt(nowDateTimeLocalValue());
          await onAdded();
        } finally {
          setSaving(false);
        }
      }}
      style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}
    >
      <div className="field" style={{ flex: 1, marginBottom: 0 }}>
        <label htmlFor="weight">体重 (kg)</label>
        <input
          id="weight"
          inputMode="decimal"
          value={w}
          onChange={(e) => setW(e.target.value)}
        />
      </div>
      <div className="field" style={{ flex: 1, marginBottom: 0 }}>
        <label htmlFor="weight-recorded-at">計測日時</label>
        <input
          id="weight-recorded-at"
          type="datetime-local"
          value={recordedAt}
          onChange={(e) => setRecordedAt(e.target.value)}
        />
      </div>
      <button className="btn-primary" disabled={saving || !w || !recordedAt}>
        記録
      </button>
    </form>
  );
}
